import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Stamp } from "lucide-react";
import { toast } from "sonner";

export default function CollectorVaultModal({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@") || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscribers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "vault" }),
      });
      const data = await res.json();
      if (data.status === "existing") {
        toast.info(`Credential ${data.credential} is already stamped to this address.`);
      } else {
        toast.success(`Collector credential ${data.credential} stamped. Welcome to the vault.`);
      }
      setEmail("");
      onClose();
    } catch {
      toast.error("The vault is unreachable — please try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="vault-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#171412]/70 px-6 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md border border-[#C59B27]/40 bg-[#FAF6F0] p-8 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] sm:p-10"
          >
            <button
              data-testid="vault-modal-close-btn"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center border border-[#E2D9CD] text-[#5C544D] transition-colors duration-300 hover:border-[#6B1D2F] hover:text-[#6B1D2F]"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="mb-6 flex h-12 w-12 items-center justify-center border border-[#C59B27]/50 bg-[#C59B27]/10">
              <Stamp className="h-5 w-5 text-[#6B1D2F]" />
            </div>

            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6B1D2F]">
              Collector Vault — Early Access
            </p>
            <h3 className="mt-3 font-serif text-3xl tracking-tight text-[#1C1815]">
              Reserve your seat<br />
              <span className="italic text-[#6B1D2F]">in the front row.</span>
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-[#5C544D]">
              Invites go out in stamped batches before opening day. Leave your address and a
              numbered collector credential will be held in your name.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4">
              <input
                data-testid="vault-modal-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@correspondence.com"
                className="w-full border border-[#1C1815] bg-[#FCF9F5] px-4 py-3.5 font-mono text-xs tracking-wider text-[#1C1815] outline-none transition-colors duration-300 placeholder:text-[#8A8075] focus:border-[#6B1D2F]"
              />
              <button
                data-testid="vault-modal-submit-btn"
                type="submit"
                disabled={submitting}
                className="w-full border border-[#6B1D2F] bg-[#6B1D2F] py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-[#FAF6F0] transition-colors duration-300 hover:bg-[#521422] disabled:opacity-60"
              >
                {submitting ? "Stamping…" : "Stamp My Credential"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
